var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios, { AxiosError } from 'axios';
import { global } from './GlobalState';
const API_BASE_URL = 'https://api.orbscape.io';
/**
 * Utility function to log API errors on the main thread.  It’s still used
 * for the fallback when workers aren’t available.
 */
function handleApiError(error) {
    var _a, _b;
    if (error instanceof AxiosError) {
        console.error('API Error:', {
            message: error.message,
            responseData: (_a = error.response) === null || _a === void 0 ? void 0 : _a.data,
            status: (_b = error.response) === null || _b === void 0 ? void 0 : _b.status,
            config: error.config,
        });
    }
    else {
        console.error('Unexpected Error:', error);
    }
}
/* --------------------------------------------------------------------------
 * Web Worker infrastructure
 *
 * Each exported method delegates to a dedicated Web Worker implemented in
 * `SystemAPIWorker.ts`. If `Worker` isn’t defined (e.g. server-side), the
 * code falls back to Axios. We also:
 *  - terminate the worker on pagehide/beforeunload (tab close or SPA nav),
 *  - terminate on HMR dispose,
 *  - terminate after an idle timeout,
 *  - reject all pending requests if we terminate early.
 */
let apiWorker = null;
let requestIdCounter = 0;
// Track pending requests to resolve/reject on response or termination
const pending = new Map();
const OPTIONS_FLAG = '__systemApiOptions';
// Idle auto-dispose (tweak as needed)
const IDLE_MS = 30000;
let idleTimer = null;
// Make sure we only attach lifecycle handlers once
let cleanupHandlersAttached = false;
function startIdleTimer() {
    stopIdleTimer();
    idleTimer = setTimeout(() => {
        terminateWorker('idle-timeout');
    }, IDLE_MS);
}
function stopIdleTimer() {
    if (idleTimer !== null) {
        clearTimeout(idleTimer);
        idleTimer = null;
    }
}
function bumpIdleTimer() {
    // Ping the idle timer any time we send/receive a request
    startIdleTimer();
}
function enqueueSystemApiOp(label, run) {
    const runtime = global.highPerformanceRuntime;
    if (!runtime) {
        return Promise.resolve().then(run);
    }
    return runtime.enqueueSystemApiOp(label, run);
}
function attachCleanupHandlers() {
    if (cleanupHandlersAttached || typeof window === 'undefined')
        return;
    cleanupHandlersAttached = true;
    // When page is hidden (tab switch) we do nothing; but on pagehide/unload, dispose.
    // pagehide fires for bfcache navigations; beforeunload is a broader catch.
    const onPageHide = () => terminateWorker('pagehide');
    const onBeforeUnload = () => terminateWorker('beforeunload');
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    // Handle Webpack/Vite HMR so we don’t leak workers across reloads
    // Webpack 5: import.meta.webpackHot; Vite: import.meta.hot
    // (guard both; harmless if not present)
    // @ts-ignore
    const hot = import.meta.hot || import.meta.webpackHot;
    if (hot && typeof hot.dispose === 'function') {
        hot.dispose(() => {
            terminateWorker('hmr-dispose');
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('beforeunload', onBeforeUnload);
            cleanupHandlersAttached = false;
        });
    }
}
function shouldUseWorker() {
    return global.dataWorkerEnabled && typeof Worker !== 'undefined';
}
function ensureWorker() {
    if (!apiWorker && shouldUseWorker()) {
        try {
            apiWorker = new Worker(new URL('./SystemAPIWorker.ts', import.meta.url), { type: 'module' });
            apiWorker.onmessage = (ev) => {
                var _a, _b;
                const { id, result, error, type, loaded, total } = (_a = ev.data) !== null && _a !== void 0 ? _a : {};
                const handler = pending.get(id);
                if (!handler)
                    return;
                if (type === 'progress') {
                    (_b = handler.progress) === null || _b === void 0 ? void 0 : _b.call(handler, { loaded: loaded !== null && loaded !== void 0 ? loaded : 0, total: total !== null && total !== void 0 ? total : 0 });
                    return;
                }
                if (error)
                    handler.reject(error);
                else
                    handler.resolve(result);
                pending.delete(id);
                bumpIdleTimer();
            };
            apiWorker.onerror = (e) => {
                console.error('SystemAPI worker error:', e);
            };
            attachCleanupHandlers();
            startIdleTimer();
        }
        catch (err) {
            console.warn('Could not start SystemAPI worker; falling back to main thread.', err);
            apiWorker = null;
        }
    }
}
function terminateWorker(reason) {
    if (!apiWorker)
        return;
    try {
        // Reject all pending requests so callers don’t hang forever
        const err = new Error(`SystemAPI worker terminated (${reason})`);
        for (const [id, handler] of pending.entries()) {
            handler.reject(err);
            pending.delete(id);
        }
        apiWorker.terminate();
    }
    catch (e) {
        console.warn('Error terminating SystemAPI worker:', e);
    }
    finally {
        apiWorker = null;
        stopIdleTimer();
    }
}
function callWorkerMethod(method, ...rawArgs) {
    return __awaiter(this, void 0, void 0, function* () {
        let options;
        let args = rawArgs;
        if (rawArgs.length) {
            const maybeOptions = rawArgs[rawArgs.length - 1];
            if (maybeOptions && typeof maybeOptions === 'object' && maybeOptions[OPTIONS_FLAG]) {
                const cloned = Object.assign({}, maybeOptions);
                delete cloned[OPTIONS_FLAG];
                options = cloned;
                args = rawArgs.slice(0, -1);
            }
        }
        ensureWorker();
        // Use worker if available
        if (apiWorker) {
            return enqueueSystemApiOp(`SystemAPI:${method}`, () => new Promise((resolve, reject) => {
                const id = ++requestIdCounter;
                pending.set(id, { resolve, reject, progress: options === null || options === void 0 ? void 0 : options.onProgress });
                try {
                    apiWorker.postMessage({ id, method, args });
                    bumpIdleTimer();
                }
                catch (e) {
                    pending.delete(id);
                    reject(e);
                }
            }));
        }
        // ---- Fallback: Axios on main thread --------------------------------------
        return enqueueSystemApiOp(`SystemAPI:${method}`, () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            switch (method) {
                case 'getLanguageOrbs': {
                    try {
                        const resp = yield axios.get(`${API_BASE_URL}/functions`);
                        const data = resp.data;
                        return Array.isArray(data) ? data : [];
                    }
                    catch (err) {
                        handleApiError(err);
                        return [];
                    }
                }
                case 'getLanguageOrbsByRepo': {
                    const [repoName] = args;
                    try {
                        const resp = yield axios.get(`${API_BASE_URL}/repositorycode/${repoName}`);
                        const data = resp.data;
                        return Array.isArray(data) ? data : [];
                    }
                    catch (err) {
                        handleApiError(err);
                        return [];
                    }
                }
                case 'getGitHubRepos': {
                    const resp = yield axios.get(`${API_BASE_URL}/systemrepositories`);
                    return resp.data;
                }
                case 'getDomains': {
                    const resp = yield axios.get(`${API_BASE_URL}/domains`);
                    return resp.data;
                }
                case 'getDockerContainers': {
                    const resp = yield axios.get(`${API_BASE_URL}/dockercontainers`);
                    return resp.data;
                }
                case 'getRegistryImageInfo': {
                    const resp = yield axios.get(`${API_BASE_URL}/containerartifacts`);
                    return resp.data;
                }
                case 'getDockerSwarmInfo': {
                    const resp = yield axios.get(`${API_BASE_URL}/dockerswarminfo`);
                    return resp.data;
                }
                case 'getKubernetesPods': {
                    const resp = yield axios.get(`${API_BASE_URL}/kubernetespods`);
                    return resp.data;
                }
                case 'getKubernetesNodes': {
                    const resp = yield axios.get(`${API_BASE_URL}/kubernetesnodes`);
                    return resp.data;
                }
                case 'getDesmosOrbs': {
                    const resp = yield axios.get(`${API_BASE_URL}/desmos`);
                    return resp.data;
                }
                case 'getMathOrbs': {
                    const resp = yield axios.get(`${API_BASE_URL}/math`);
                    return resp.data;
                }
                case 'getContainerEndpoints': {
                    const resp = yield axios.get(`${API_BASE_URL}/endpoints`);
                    return resp.data;
                }
                case 'getChatbots': {
                    const resp = yield axios.get(`${API_BASE_URL}/chatbots`);
                    return (_b = (_a = resp.data) === null || _a === void 0 ? void 0 : _a.output) !== null && _b !== void 0 ? _b : [];
                }
                case 'getPrompts': {
                    const resp = yield axios.get(`${API_BASE_URL}/prompts`);
                    return resp.data;
                }
                case 'getMathIdentity': {
                    const resp = yield axios.get(`${API_BASE_URL}/mathidentities`);
                    return (_d = (_c = resp.data) === null || _c === void 0 ? void 0 : _c.output) !== null && _d !== void 0 ? _d : [];
                }
                case 'getTextObjects': {
                    const resp = yield axios.get(`${API_BASE_URL}/textobjects`);
                    return (_f = (_e = resp.data) === null || _e === void 0 ? void 0 : _e.output) !== null && _f !== void 0 ? _f : [];
                }
                case 'getDatabaseObjects': {
                    const resp = yield axios.get(`${API_BASE_URL}/databaseobjects`);
                    return (_g = resp.data) !== null && _g !== void 0 ? _g : [];
                }
                case 'getNetworkGateways': {
                    const resp = yield axios.get(`${API_BASE_URL}/gatewayconfiguration`);
                    return (_h = resp.data) !== null && _h !== void 0 ? _h : [];
                }
                case 'getAllTextEntries': {
                    const resp = yield axios.get(`${API_BASE_URL}/GetTextStore`);
                    return resp.data;
                }
                case 'addText': {
                    const [text] = args;
                    const { data } = yield axios.post(`${API_BASE_URL}/AddTextStore`, { text });
                    return data;
                }
                case 'updateText': {
                    const [id, text] = args;
                    const { data } = yield axios.put(`${API_BASE_URL}/UpdateTextStore/${id}`, { text });
                    return data;
                }
                case 'deleteText': {
                    const [id] = args;
                    const { data } = yield axios.delete(`${API_BASE_URL}/DeleteTextStore/${id}`);
                    return data;
                }
                default:
                    throw new Error(`Unknown SystemAPI method ${method}`);
            }
        }));
    });
}
// Exported API: each function just forwards its name and arguments to callWorkerMethod.
export function getServiceIcons() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('serviceIcons');
    });
}
export function getLanguageOrbs() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getLanguageOrbs');
    });
}
export function getLanguageOrbsByRepo(repoName) {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getLanguageOrbsByRepo', repoName);
    });
}
export function getGitHubRepos() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getGitHubRepos');
    });
}
export function getDockerContainers() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getDockerContainers');
    });
}
export function getRegistryImageInfo() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getRegistryImageInfo');
    });
}
export function getDockerSwarmInfo() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getDockerSwarmInfo');
    });
}
export function getDomains() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getDomains');
    });
}
export function getKubernetesPods() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getKubernetesPods');
    });
}
export function getKubernetesNodes() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getKubernetesNodes');
    });
}
export function getDesmosOrbs() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getDesmosOrbs');
    });
}
export function getContainerEndpoints() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getContainerEndpoints');
    });
}
export function getChatbots() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getChatbots');
    });
}
export function getPrompts() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getPrompts');
    });
}
export function getMathOrbs() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getMathOrbs');
    });
}
export function getTextObjects() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getTextObjects');
    });
}
export function getDatabaseObjects() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getDatabaseObjects');
    });
}
export function getNetworkGateways() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getNetworkGateways');
    });
}
export function getAllTextEntries() {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('getAllTextEntries');
    });
}
export function addText(text) {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('addText', text);
    });
}
export function updateText(id, text) {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('updateText', id, text);
    });
}
export function deleteText(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return callWorkerMethod('deleteText', id);
    });
}
export function withSystemApiOptions(options) {
    return Object.assign(Object.assign({}, options), { [OPTIONS_FLAG]: true });
}
/*
class GraphQlCommandGateway {
  private endpoint: string;
  private defaultHeaders: Record<string, string>;

  constructor(endpoint: string, defaultHeaders: Record<string, string> = {}) {
    this.endpoint = endpoint;
    this.defaultHeaders = defaultHeaders;
  }

  public withHeader(key: string, value: string): GraphQlCommandGateway {
    return new GraphQlCommandGateway(this.endpoint, { ...this.defaultHeaders, [key]: value });
  }

  public async execute(operationName: string, query: string, variables: Record<string, unknown> = {}): Promise<any> {
    // TODO: Replace with production-grade GraphQL transport to orchestrate orb graphs
    return Promise.resolve({ operationName, queryLength: query.length, variables });
  }
}
*/
